import { useLazyQuery, useMutation }                    from '@apollo/client'
import { ArrowUturnLeftIcon }                           from '@heroicons/react/24/solid'
import { useEffect, useState }                          from 'react'
import { useDispatch, useSelector }                     from 'react-redux'
import { useNavigate }                                  from 'react-router-dom'
import { Button, TextField }                            from '../'
import { SelectOption }                                 from '../../components/Select/Select'
import UserAvatar                                       from '../../components/User/UserAvatar'
import { setToasts, showToaster }                       from '../../features/toaster/toasterSlice'
import { setAccountTypes, setLocations }                from '../../features/user/currentUserSlice'
import { ACCOUNT_TYPES }                                from '../../graphql/account_type'
import { GET_USERS_LOCATIONS }                          from '../../graphql/location'
import { DELETE_AVATAR_FILE, UPDATE_USER, CREATE_USER, DELETE_USER } from '../../graphql/user'
import Api                                              from '../../utilities/axios'
import { LoadingEllipsis }                              from '../Loaders'
import { Switch }                                       from '../Switch'

export default function UserEditForm( props: any ): JSX.Element {
  const uploadApiUrl: string              = process.env.REACT_APP_UPLOAD_HOST
  const { user }                          = props
  const navigate: any                     = useNavigate()
  const dispatch: any                     = useDispatch()
  const currentTenant: any                = useSelector( ( state: any ): any => state.currentTenant.tenant )
  const currentUser: any                  = useSelector( ( state: any ): any => state.currentUser.user )
  const locations: any                    = useSelector( ( state: any ): any => state.currentUser.locations )
  const accountTypes: any                 = useSelector( ( state: any ): any => state.currentUser.accountTypes )
  const [ fileToUpload, setFileToUpload ] = useState()
  const [ formData, setFormData ]         = useState( {
                                                        name:         user?.name || '',
                                                        email:        user?.email || '',
                                                        password:     '',
                                                        admin:        user?.admin || false,
                                                        director:     user?.director || false,
                                                        strictAccess: user?.strict_access || false,
                                                        role:         user?.role || '',
                                                        userLocation: ( user && locations ) ? locations.find( ( l: any ): any => l.id === user.location_id ) : null,
                                                        accountType:  ( user && accountTypes ) ? accountTypes.find( ( t: any ): any => t.id === user.account_type_id ) : null
                                                      } )
  const [ errors, setErrors ]             = useState( {
                                                        name:         false,
                                                        email:        false,
                                                        password:     false,
                                                        role:         false,
                                                        accountType:  false,
                                                        userLocation: false
                                                      } )
  const roleOptions: any                  = [
    { key: 'ADMIN', value: 'ADMIN' },
    { key: 'USER', value: 'USER' }
  ]

  const updateField: any = ( field: keyof typeof formData, value: string ): any => {
    setFormData( prev => ( { ...prev, [ field ]: value } ) )
  }

  const checkErrors: any = (): boolean => {
    const emailRegex: any = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    const passwordRules = {
      hasLowercase: /[a-z]/.test( formData.password ),
      hasUppercase: /[A-Z]/.test( formData.password ),
      hasNumber:    /[0-9]/.test( formData.password ),
      hasSymbol:    /[\!\@\£\#\$\%\^\&\*\(\)\-\_\+\=\[\{\]\}\|\\\"\'\;\:\/\?\.\>\,\<\`\~]/.test( formData.password )
    }

    const newErrors = {
      name:         !formData.name.trim(),
      email:        ( formData.email.trim().length < 7 || !formData.email.trim().match( emailRegex ) ),
      password:     ( !user && !formData.password.trim().length ) || ( formData.password.trim().length > 0 && (
        formData.password.length < 6
          || formData.password.length > 32
          || !passwordRules.hasLowercase
          || !passwordRules.hasUppercase
          || !passwordRules.hasNumber
          || !passwordRules.hasSymbol
      ) ),
      role:         !formData.role,
      accountType:  !formData.accountType,
      userLocation: !formData.userLocation
    }

    setErrors( newErrors )

    return Object.values( newErrors ).some( Boolean )
  }

  const [ createUser, { loading: creating } ] = useMutation( CREATE_USER )

  const [ updateUser, { loading: updating } ] = useMutation( UPDATE_USER, {
    onCompleted: (): void => dispatchMessage( 'success', 'User updated!' ),
    onError:     (): void => dispatchMessage( 'error', 'Update failed. Please try again' )
  } )

  const [ deleteUser, { loading: deleting } ] = useMutation( DELETE_USER, {
    onCompleted: ( { deleteUser }: any ): void => {
      if ( deleteUser?.__typename === 'FieldError' ) {
        dispatchMessage( 'error', deleteUser.message ?? 'User NOT deleted. Please try again' )
        return
      }

      dispatchMessage( 'success', 'User deleted!' )
      navigate( '/users' )
    },
    onError: (): void => dispatchMessage( 'error', 'User NOT deleted. Please try again' )
  } )

  const [ deleteAvatarFile ] = useMutation( DELETE_AVATAR_FILE, {
    onError: ( e: any ): any => console.error( 'old avatar not deleted: ', e )
  } )

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  const handleFileChange: any = async ( e: any ): Promise<void> => {
    if ( e.target.files ) {
      const file: any = e.target.files[ 0 ]

      if ( file.size > 1048576 ) {
        alert( 'Max file size is 1MB. File NOT uploaded!' )
      } else {
        setFileToUpload( file )
      }
    }
  }

  const uploadAvatar: any = async ( userId: string ): Promise<any> => {
    const formData: FormData = new FormData()

    formData.append( 'file', fileToUpload )
    formData.append( 'is_public', 'true' )
    formData.append( 'folder', currentTenant.uuid + '/user/' + userId + '/avatar' )

    try {
      const { data } = await Api( uploadApiUrl, 'multipart/form-data' ).post( '/', formData )

      return data[ 0 ]?.path?.length ? data[ 0 ].path : null
    } catch ( error ) {
      console.error( `Error uploading file`, error )
      return null
    }
  }

  const handleSubmit: any = async (): Promise<void> => {
    if ( await checkErrors() ) return

    const input = {
      name:            formData.name,
      email:           formData.email,
      admin:           formData.admin,
      director:        formData.director,
      strict_access:   formData.strictAccess,
      role:            formData.role,
      location_id:     formData.userLocation.id ?? formData.userLocation,
      account_type_id: formData.accountType.id ?? formData.accountType
    }

    if ( formData.password.length > 0 ) {
      if ( user && !window.confirm( 'Are you sure you want to change the password for this user?' ) ) {
        return
      }

      // @ts-ignore
      input.password = formData.password
    }

    try {
      if ( user?.id ) {
        if ( fileToUpload ) {
          // @ts-ignore
          input.avatar_url = await uploadAvatar()

          //@ts-ignore
          if ( input.avatar_url && user?.avatar_url?.length ) {
            await deleteAvatarFile( { variables: { input: { path: user.avatar_url } } } )
          }
        }

        await updateUser( { variables: { input: { ...input, id: user.id } } } )
      } else {
        const { data }: any = await createUser( { variables: { input } } )

        if ( data?.createUser?.__typename !== 'MutationCreateUserSuccess' ) {
          dispatchMessage( 'error', data?.createUser?.message ?? 'User NOT added. Please try again' )
          return
        }

        dispatchMessage( 'success', 'User created!' )
        navigate( '/users' )
      }
    } catch ( e ) {
      console.log( 'e: ', e )
      if ( !user?.id ) dispatchMessage( 'error', 'User NOT added. Please try again' )
    }
  }

  const handleDelete: any = async (): Promise<void> => {
    if ( !user?.id ) return

    if ( !window.confirm( 'Are you sure you want to delete this user?' ) ) {
      return
    }

    await deleteUser( { variables: { input: { id: user.id, soft_delete: true } } } )
  }

  const [ getAccountTypes, { loading, error } ] = useLazyQuery( ACCOUNT_TYPES, {
    onCompleted: ( { accountTypes }: any ): void => {
      user && updateField( 'accountType', accountTypes.find( ( at: any ): any => at.id === user.account_type_id ) )
      dispatch( setAccountTypes( accountTypes ) )
    },
    onError:     ( error: any ): void => console.log( error )
  } )

  const [ getLocations, { loading: loadingLocations, error: errorLocations } ] = useLazyQuery( GET_USERS_LOCATIONS, {
    onCompleted: ( { usersLocations }: any ): void => {
      user && updateField( 'userLocation', usersLocations.find( ( l: any ): any => l.id === user.location_id ) )
      dispatch( setLocations( usersLocations ) )
    },
    onError:     ( error: any ): void => console.log( error )
  } )

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch ) {
      if ( !accountTypes?.length ) {
        getAccountTypes()
      }

      if ( !locations?.length ) {
        getLocations()
      }
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ accountTypes, locations ] )

  return (
    <>
      <div className="w-24 my-4">
        <Button
          label="Back"
          blue
          onClick={ (): any => navigate( -1 ) }
          icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
        />
      </div>

      { ( creating || updating || deleting || loading || loadingLocations || error || errorLocations ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

      { !creating && !updating && !deleting && !loading && !loadingLocations && !error && !errorLocations &&
        <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 xl:p-5">
          { user &&
            <div className="flex flex-wrap items-center w-full mb-3">
              <div className="w-1/3">Avatar</div>
              <div className="w-2/3">
                <UserAvatar user={ user } />

                <div className="mb-4">
                  <input id="file" type="file" accept="image/*" onChange={ handleFileChange } />
                </div>
              </div>
            </div>
          }

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Name</div>
            <div className="w-2/3">
              <TextField
                label={ 'Name' }
                value={ formData.name }
                name="title"
                type="text"
                onChange={ ( val: string ): any => updateField( 'name', val ) }
                errorField={ errors.name ? 'name' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Email</div>
            <div className="w-2/3">
              <TextField
                label={ 'Email' }
                value={ formData.email }
                name="email"
                type="text"
                onChange={ ( val: string ): any => updateField( 'email', val ) }
                errorField={ errors.email ? 'email' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Password</div>
            <div className="w-2/3">
              <TextField
                label={ 'Password' }
                value={ formData.password }
                name="password"
                type="password"
                autoComplete="new-password"
                onChange={ ( val: string ): any => updateField( 'password', val ) }
                errorField={ errors.password ? 'password' : '' }
                isRequired={ !user }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Director/Manager</div>
            <div className="w-2/3">
              <Switch
                checked={ formData.director }
                onChange={ ( val: string ): any => updateField( 'director', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Admin</div>
            <div className="w-2/3">
              <Switch
                checked={ formData.admin }
                onChange={ ( val: string ): any => updateField( 'admin', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Strict Access</div>
            <div className="w-2/3">
              <Switch
                checked={ formData.strictAccess }
                onChange={ ( val: string ): any => updateField( 'strictAccess', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Role</div>
            <div className="w-2/3">
              <div className="flex">
                <SelectOption
                  setSelected={ ( val: string ): any => updateField( 'role', val ) }
                  selected={ formData.role }
                  placeholder="Role"
                  errorField={ errors.role }
                  required
                  wrapperClasses="text-grey-input rounded-2xl border border-grey-light overflow-hidden"
                  fieldClasses={ `border-none focus:ring-0 ${ errors.role ? 'ring-1 ring-red-500' : '' }` }
                  options={ roleOptions }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Account Type</div>
            <div className="w-2/3">
              <div className="flex">
                <SelectOption
                  setSelected={ ( val: string ): any => updateField( 'accountType', val ) }
                  selected={ formData.accountType }
                  placeholder={ formData.accountType?.name }
                  errorField={ errors.accountType }
                  required
                  wrapperClasses="text-grey-input rounded-2xl border border-grey-light overflow-hidden"
                  fieldClasses={ `border-none focus:ring-0 ${ errors.accountType ? 'ring-1 ring-red-500' : '' }` }
                  options={ accountTypes.map( ( sort: any ): any => {
                    return { key: sort.id, value: sort.name }
                  } ) }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Location</div>
            <div className="w-2/3">
              <div className="flex">
                <SelectOption
                  setSelected={ ( val: string ): any => updateField( 'userLocation', val ) }
                  selected={ formData.userLocation }
                  placeholder={ formData.userLocation?.name }
                  errorField={ errors.userLocation }
                  required
                  wrapperClasses="text-grey-input rounded-2xl border border-grey-light overflow-hidden"
                  fieldClasses={ `border-none focus:ring-0 ${ errors.userLocation ? 'ring-1 ring-red-500' : '' }` }
                  options={ locations.map( ( sort: any ): any => {
                    return { key: sort.id, value: sort.name }
                  } ) }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap w-full mt-3">
            <div className="flex pt-5 gap-3">
              <Button label="Submit" yellow auto onClick={ handleSubmit } />
              { user?.id && currentUser?.admin && currentUser?.id !== user.id &&
                <Button label="Delete" red auto onClick={ handleDelete } />
              }
            </div>
          </div>
        </div>
      }
    </>
  )
}
